import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, keepPreviousData } from "@tanstack/react-query";
import { useState } from "react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2, ShieldCheck, XCircle, FileText, Clock, AlertTriangle,
  CheckCircle2, ShieldAlert, ExternalLink, Ban, Upload, RotateCcw, Eye,
  Download, Printer, Search, ArrowUpDown,
} from "lucide-react";
import { adminGetVerificationHistory, getDocSignedUrl } from "@/lib/helper-kyc.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/kyc/$helperId")({
  head: () => ({
    meta: [
      { title: "Verification timeline — Peopels admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: TimelinePage,
});

type Attempt = {
  id: string;
  attempt_number: number;
  status: string;
  submitted_at: string | null;
  decided_at: string | null;
  decision: string | null;
  decision_code: string | null;
  reviewer_id: string | null;
  reviewer_notes: string | null;
  document_ids: string[] | null;
  created_at: string;
};
type AuditEvent = {
  id: string;
  attempt_id: string | null;
  attempt_number: number | null;
  event_type: string;
  actor_id: string | null;
  actor_role: string | null;
  from_status: string | null;
  to_status: string | null;
  decision_code: string | null;
  notes: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
};
type DocRow = {
  id: string;
  doc_type: string;
  status: string;
  attempt_number: number;
  original_filename: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  scan_status: string;
  decision_code: string | null;
  reviewer_notes: string | null;
  reviewed_at: string | null;
  superseded_at: string | null;
  superseded_by: string | null;
  created_at: string;
};

const PAGE_SIZE = 50;

const EVENT_META: Record<string, { label: string; icon: typeof CheckCircle2; tone: string }> = {
  submitted: { label: "Helper submitted for review", icon: Upload, tone: "text-sky-700 bg-sky-50" },
  approved: { label: "Approved by admin", icon: CheckCircle2, tone: "text-emerald-700 bg-emerald-50" },
  rejected: { label: "Rejected by admin", icon: XCircle, tone: "text-rose-700 bg-rose-50" },
  more_info_required: { label: "More info requested", icon: AlertTriangle, tone: "text-amber-700 bg-amber-50" },
  document_uploaded: { label: "Document uploaded", icon: FileText, tone: "text-slate-700 bg-slate-100" },
  document_resubmitted: { label: "Document resubmitted", icon: RotateCcw, tone: "text-indigo-700 bg-indigo-50" },
  document_quarantined: { label: "Upload blocked — failed safety scan", icon: ShieldAlert, tone: "text-rose-700 bg-rose-50" },
  resubmission_blocked: { label: "Upload blocked — rate limit", icon: Ban, tone: "text-rose-700 bg-rose-50" },
};

function eventMeta(t: string) {
  return EVENT_META[t] ?? { label: t, icon: ShieldCheck, tone: "text-slate-700 bg-slate-100" };
}

function statusPill(status: string) {
  const tones: Record<string, string> = {
    submitted: "bg-sky-50 text-sky-700",
    approved: "bg-emerald-50 text-emerald-700",
    rejected: "bg-rose-50 text-rose-700",
    superseded: "bg-slate-100 text-slate-600",
    more_info_required: "bg-amber-50 text-amber-700",
    pending: "bg-sky-50 text-sky-700",
  };
  return tones[status] ?? "bg-slate-100 text-slate-700";
}

function fmtSize(n: number | null): string {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

function TimelinePage() {
  const { helperId } = Route.useParams();
  const fetchHistory = useServerFn(adminGetVerificationHistory);
  const signDoc = useServerFn(getDocSignedUrl);

  // ----- Filters / pagination / sort (server-side) -----
  const [q, setQ] = useState("");
  const [fEvent, setFEvent] = useState<string>("all");
  const [fStatus, setFStatus] = useState<string>("all");
  const [fDocType, setFDocType] = useState<string>("all");
  const [fScan, setFScan] = useState<string>("all");
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<"asc" | "desc">("desc");

  const queryInput = {
    helperId,
    auditLimit: PAGE_SIZE,
    auditOffset: page * PAGE_SIZE,
    auditSort: sort,
    filterEventType: fEvent,
    filterStatus: fStatus,
    filterDocType: fDocType,
    filterScanVerdict: fScan,
    search: q.trim() || undefined,
  };

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["admin-verification-history", queryInput],
    queryFn: () => fetchHistory({ data: queryInput }),
    placeholderData: keepPreviousData,
  });

  const viewDoc = useMutation({
    mutationFn: async (docId: string) => signDoc({ data: { docId } }),
    onSuccess: (r) => { if (r?.url) window.open(r.url, "_blank", "noopener"); },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Could not open document"),
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="mx-auto max-w-4xl px-4 py-16 text-center text-muted-foreground">
          <Loader2 className="mx-auto h-6 w-6 animate-spin" />
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="mx-auto max-w-4xl px-4 py-16 text-center text-rose-600">
          {error instanceof Error ? error.message : "Could not load history"}
        </main>
        <Footer />
      </div>
    );
  }

  const attempts = (data?.attempts ?? []) as Attempt[];
  const audit = (data?.audit ?? []) as AuditEvent[];
  const docs = (data?.documents ?? []) as DocRow[];
  const docsById = new Map(docs.map((d) => [d.id, d]));
  const auditTotal = data?.auditTotal ?? 0;
  const facets = (data?.facets ?? { eventTypes: [], statusTypes: [], docTypes: [] }) as {
    eventTypes: string[]; statusTypes: string[]; docTypes: string[];
  };
  const totalPages = Math.max(1, Math.ceil(auditTotal / PAGE_SIZE));

  function resetPage<T>(setter: (v: T) => void) {
    return (v: T) => { setPage(0); setter(v); };
  }

  function scanVerdictOf(e: AuditEvent): string | null {
    const m = e.metadata as { scan?: { verdict?: string } } | null;
    return m?.scan?.verdict ?? null;
  }
  function docTypeOf(e: AuditEvent): string | null {
    const m = e.metadata as { doc_type?: string } | null;
    return m?.doc_type ?? null;
  }

  // ----- Exports (uses CURRENT filters + sort; fetches all matching pages) -----
  function csvEscape(v: unknown): string {
    if (v === null || v === undefined) return "";
    const s = typeof v === "string" ? v : JSON.stringify(v);
    if (/[",\n]/.test(s)) return `"${s.replaceAll('"', '""')}"`;
    return s;
  }

  async function fetchAllFilteredAudit(): Promise<AuditEvent[]> {
    const out: AuditEvent[] = [];
    const PAGE = 200;
    for (let offset = 0; offset < auditTotal; offset += PAGE) {
      const r = await fetchHistory({
        data: { ...queryInput, auditLimit: PAGE, auditOffset: offset },
      });
      out.push(...((r?.audit ?? []) as AuditEvent[]));
      if (!r?.audit || (r.audit as AuditEvent[]).length < PAGE) break;
    }
    return out;
  }

  async function downloadCsv() {
    const all = await fetchAllFilteredAudit();
    const headers = [
      "section","created_at","attempt_number","event_type","actor_role","from_status","to_status",
      "decision_code","doc_type","scan_provider","scan_verdict","scan_reason","scanned_at","notes","metadata",
    ];
    const rows: string[][] = [];
    for (const e of all) {
      const m = (e.metadata ?? {}) as { scan?: Record<string, unknown>; doc_type?: string };
      rows.push([
        "audit", e.created_at, String(e.attempt_number ?? ""), e.event_type, e.actor_role ?? "",
        e.from_status ?? "", e.to_status ?? "", e.decision_code ?? "", m.doc_type ?? "",
        String(m.scan?.provider ?? ""), String(m.scan?.verdict ?? ""), String(m.scan?.reason ?? ""),
        String(m.scan?.scanned_at ?? ""), e.notes ?? "", JSON.stringify(e.metadata ?? {}),
      ]);
    }
    const docHeaders = ["section","doc_id","doc_type","status","attempt_number","scan_status","decision_code","original_filename","mime_type","size_bytes","created_at","superseded_at","superseded_by","reviewer_notes"];
    rows.push([]);
    rows.push(docHeaders);
    for (const d of docs) {
      rows.push([
        "document", d.id, d.doc_type, d.status, String(d.attempt_number), d.scan_status,
        d.decision_code ?? "", d.original_filename ?? "", d.mime_type ?? "",
        String(d.size_bytes ?? ""), d.created_at, d.superseded_at ?? "", d.superseded_by ?? "",
        d.reviewer_notes ?? "",
      ]);
    }
    const filterHeader = [
      `# Verification timeline export`,
      `# helper_id=${helperId}`,
      `# filters: event=${fEvent} status=${fStatus} doc_type=${fDocType} scan=${fScan} search=${q.trim()}`,
      `# sort=${sort} total_audit_rows=${all.length}`,
    ].join("\n");
    const csv = filterHeader + "\n" + [headers, ...rows].map((r) => r.map(csvEscape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `verification-timeline-${helperId}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`CSV downloaded (${all.length} events)`);
  }
  function printPdf() {
    window.print();
  }

  const hasFilters = fEvent !== "all" || fStatus !== "all" || fDocType !== "all" || fScan !== "all" || q.trim().length > 0;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-4xl px-4 py-8 print:max-w-full print:py-2">
        <style>{`@media print { header, footer, .no-print { display: none !important; } body { background: white; } }`}</style>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <Link to="/admin/kyc" className="text-sm text-muted-foreground hover:text-foreground no-print">
              ← Back to KYC queue
            </Link>
            <h1 className="mt-1 text-xl font-semibold">Verification timeline</h1>
            <p className="text-xs text-muted-foreground font-mono">helper {helperId}</p>
          </div>
          <div className="flex flex-wrap gap-2 no-print">
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
              {isFetching ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={downloadCsv}>
              <Download className="mr-1.5 h-4 w-4" /> CSV (filtered)
            </Button>
            <Button variant="outline" size="sm" onClick={printPdf}>
              <Printer className="mr-1.5 h-4 w-4" /> PDF
            </Button>
          </div>
        </div>

        {/* Filters */}
        <section className="no-print mb-6 rounded-2xl border bg-white p-4 shadow-sm">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
            <label className="relative block sm:col-span-2">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => { setPage(0); setQ(e.target.value); }}
                placeholder="Search notes, codes, statuses…"
                className="pl-8"
                aria-label="Search timeline"
              />
            </label>
            <select value={fEvent} onChange={(e) => resetPage(setFEvent)(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
              <option value="all">All actions</option>
              {facets.eventTypes.map((t) => <option key={t} value={t}>{t.replaceAll("_", " ")}</option>)}
            </select>
            <select value={fStatus} onChange={(e) => resetPage(setFStatus)(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
              <option value="all">Any status transition</option>
              {facets.statusTypes.map((t) => <option key={t} value={t}>{t.replaceAll("_", " ")}</option>)}
            </select>
            <select value={fDocType} onChange={(e) => resetPage(setFDocType)(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
              <option value="all">Any doc type</option>
              {facets.docTypes.map((t) => <option key={t} value={t}>{t.replaceAll("_", " ")}</option>)}
            </select>
            <select value={fScan} onChange={(e) => resetPage(setFScan)(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
              <option value="all">Any scan verdict</option>
              <option value="clean">clean</option>
              <option value="malicious_or_invalid">malicious or invalid</option>
              <option value="scan_error">scan error</option>
            </select>
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>
              Showing {audit.length === 0 ? 0 : page * PAGE_SIZE + 1}–{page * PAGE_SIZE + audit.length} of {auditTotal} events
              {hasFilters ? (
                <button onClick={() => { setQ(""); setFEvent("all"); setFStatus("all"); setFDocType("all"); setFScan("all"); setPage(0); }} className="ml-2 underline">Clear filters</button>
              ) : null}
            </span>
            <button
              onClick={() => { setPage(0); setSort((s) => (s === "asc" ? "desc" : "asc")); }}
              className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-2 py-1 hover:bg-accent"
            >
              <ArrowUpDown className="h-3 w-3" />
              Sort: {sort === "desc" ? "Newest first" : "Oldest first"}
            </button>
          </div>
        </section>

        {/* Attempts summary */}
        <section className="mb-8">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Attempts</h2>
          {attempts.length === 0 ? (
            <p className="rounded-2xl border bg-white p-6 text-sm text-muted-foreground">No attempts yet.</p>
          ) : (
            <div className="space-y-3">
              {attempts.map((a) => (
                <div key={a.id} className="rounded-2xl border bg-white p-4 shadow-sm">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="rounded-lg bg-slate-900/5 px-2.5 py-1 text-xs font-semibold">
                        Attempt #{a.attempt_number}
                      </span>
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusPill(a.status)}`}>
                        {a.status.replaceAll("_", " ")}
                      </span>
                      {a.decision_code ? (
                        <span className="text-xs text-muted-foreground font-mono">{a.decision_code}</span>
                      ) : null}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {a.submitted_at ? `Submitted ${fmtTime(a.submitted_at)}` : "—"}
                      {a.decided_at ? ` · Decided ${fmtTime(a.decided_at)}` : ""}
                    </div>
                  </div>
                  {a.reviewer_notes ? (
                    <p className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
                      <span className="font-medium">Reviewer notes:</span> {a.reviewer_notes}
                    </p>
                  ) : null}
                  {a.document_ids && a.document_ids.length ? (
                    <p className="mt-2 text-xs text-muted-foreground">
                      {a.document_ids.length} document{a.document_ids.length === 1 ? "" : "s"} in this attempt
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Document versions */}
        <section className="mb-8">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Document versions ({docs.length})
          </h2>
          {docs.length === 0 ? (
            <p className="rounded-2xl border bg-white p-6 text-sm text-muted-foreground">No documents uploaded.</p>
          ) : (
            <div className="overflow-hidden rounded-2xl border bg-white">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="p-3 text-left">Type</th>
                    <th className="p-3 text-left">Attempt</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">Scan</th>
                    <th className="p-3 text-left">File</th>
                    <th className="p-3 text-left">Uploaded</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((d) => (
                    <tr key={d.id} className="border-t">
                      <td className="p-3 font-medium">{d.doc_type.replaceAll("_", " ")}</td>
                      <td className="p-3">#{d.attempt_number}</td>
                      <td className="p-3">
                        <span className={`rounded-full px-2 py-0.5 text-xs ${statusPill(d.status)}`}>
                          {d.status}
                        </span>
                      </td>
                      <td className="p-3">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs ${
                            d.scan_status === "clean"
                              ? "bg-emerald-50 text-emerald-700"
                              : d.scan_status === "quarantined"
                                ? "bg-rose-50 text-rose-700"
                                : "bg-slate-100 text-slate-700"
                          }`}
                        >
                          {d.scan_status}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="text-xs">{d.original_filename ?? "—"}</div>
                        <div className="text-xs text-muted-foreground">{d.mime_type ?? ""} · {fmtSize(d.size_bytes)}</div>
                      </td>
                      <td className="p-3 text-xs text-muted-foreground">{fmtTime(d.created_at)}</td>
                      <td className="p-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={viewDoc.isPending}
                          onClick={() => viewDoc.mutate(d.id)}
                        >
                          <Eye className="mr-1.5 h-4 w-4" />
                          View
                          <ExternalLink className="ml-1 h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Full audit timeline */}
        <section>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Audit timeline
          </h2>
          {audit.length === 0 ? (
            <p className="rounded-2xl border bg-white p-6 text-sm text-muted-foreground">No events match the current filters.</p>
          ) : (
            <ol className="relative space-y-3 border-l border-slate-200 pl-6">
              {audit.map((e) => {
                const meta = eventMeta(e.event_type);
                const Icon = meta.icon;
                const scan = (e.metadata && typeof e.metadata === "object" && "scan" in e.metadata
                  ? (e.metadata as { scan?: Record<string, unknown> }).scan
                  : null) as Record<string, unknown> | null;
                const docTypeMeta = docTypeOf(e);
                const docId = (e.metadata && typeof e.metadata === "object" && "document_id" in e.metadata
                  ? (e.metadata as { document_id?: string }).document_id
                  : null);
                const supersededIds = (e.metadata && typeof e.metadata === "object" && "superseded_document_ids" in e.metadata
                  ? (e.metadata as { superseded_document_ids?: string[] }).superseded_document_ids
                  : null);
                const verdict = scanVerdictOf(e);
                return (
                  <li key={e.id} className="relative rounded-2xl border bg-white p-4 shadow-sm">
                    <span className={`absolute -left-[34px] grid h-7 w-7 place-items-center rounded-full border-2 border-white ${meta.tone}`}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold">{meta.label}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {e.actor_role ? `${e.actor_role} · ` : ""}
                          {e.attempt_number ? `attempt #${e.attempt_number} · ` : ""}
                          {fmtTime(e.created_at)}
                        </p>
                      </div>
                      {e.from_status || e.to_status ? (
                        <div className="text-xs text-muted-foreground">
                          {e.from_status ?? "—"} → <span className="font-medium text-foreground">{e.to_status ?? "—"}</span>
                        </div>
                      ) : null}
                    </div>
                    {docTypeMeta ? (
                      <p className="mt-2 text-xs text-muted-foreground">
                        Document type: <span className="font-medium text-foreground">{docTypeMeta.replaceAll("_", " ")}</span>
                      </p>
                    ) : null}
                    {e.decision_code ? (
                      <p className="mt-1 text-xs text-muted-foreground font-mono">code: {e.decision_code}</p>
                    ) : null}
                    {e.notes ? (
                      <p className="mt-2 rounded-lg bg-slate-50 p-2.5 text-sm text-slate-700">{e.notes}</p>
                    ) : null}

                    {scan ? (
                      <div className="mt-3 grid gap-2 rounded-lg border bg-slate-50/60 p-3 text-xs sm:grid-cols-3">
                        <div>
                          <div className="text-muted-foreground">Provider</div>
                          <div className="font-mono">{String(scan.provider ?? "—")}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Verdict</div>
                          <div className={`font-semibold ${
                            verdict === "clean" ? "text-emerald-700"
                              : verdict === "scan_error" ? "text-amber-700"
                              : "text-rose-700"
                          }`}>{String(verdict ?? "—")}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Scanned at</div>
                          <div>{scan.scanned_at ? fmtTime(String(scan.scanned_at)) : "—"}</div>
                        </div>
                        {scan.reason ? (
                          <div className="sm:col-span-3">
                            <div className="text-muted-foreground">Reason</div>
                            <div>{String(scan.reason)}</div>
                          </div>
                        ) : null}
                      </div>
                    ) : null}

                    {(docId && docsById.has(docId)) || (supersededIds && supersededIds.length) ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {docId && docsById.has(docId) ? (
                          <Button size="sm" variant="outline" onClick={() => viewDoc.mutate(docId)} disabled={viewDoc.isPending}>
                            <FileText className="mr-1.5 h-3.5 w-3.5" />
                            Open new version
                          </Button>
                        ) : null}
                        {supersededIds && supersededIds.length
                          ? supersededIds.map((sid) =>
                              docsById.has(sid) ? (
                                <Button key={sid} size="sm" variant="ghost" onClick={() => viewDoc.mutate(sid)} disabled={viewDoc.isPending}>
                                  <Clock className="mr-1.5 h-3.5 w-3.5" />
                                  Prior version
                                </Button>
                              ) : null,
                            )
                          : null}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ol>
          )}

          {totalPages > 1 ? (
            <div className="no-print mt-6 flex items-center justify-between gap-3">
              <Button variant="outline" size="sm" disabled={page === 0 || isFetching} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                Previous
              </Button>
              <span className="text-xs text-muted-foreground">
                Page {page + 1} of {totalPages}
              </span>
              <Button variant="outline" size="sm" disabled={page + 1 >= totalPages || isFetching} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </div>
          ) : null}
        </section>
      </main>
      <Footer />
    </div>
  );
}
